import { z } from "zod";

import http from "@/services/http";

const answerOption = z.object({
  id: z.number().optional(),
  value: z.string().optional(),
  created_date: z.iso.datetime().optional(),
});

const questionOption = z.object({
  id: z.number(),
  name: z.string().optional(),
  value: z.string(),
});

const questionsOptions = z.object({
  questionId: z.number(),
  telemetry_keyword: z.string(),
  answers: z.array(answerOption).optional(),
});

const questions = z.object({
  id: z.number(),
  name: z.string(),
  title: z.string(),
  category: z.string(),
  telemetry_keyword: z.string(),
  type: z.string(),
  options: z.array(questionOption),
});

const surveyDetails = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  questions: z.array(questions),
});

export const surveys = z.array(surveyDetails);

export const surveyResponse = z.object({
  surveyId: z.number(),
  userId: z.number(),
  questions: z.array(questionsOptions),
});

const updateSurveyParams = z.object({
  // dto.UserSurveyResponse.UserID is a Go uint - the server ignores this value (it re-derives
  // the user from the JWT) but a JSON string here fails json.Unmarshal and yields a 500.
  userId: z.number(),
  surveyId: z.number(),
  questions: z.array(
    z
      .object({
        questionId: z.number(),
        // dto.UserSurveyResponseAnswers{id, value, created_date?} - matches answerOption's
        // real shape, not a plain string (PreferredOptions[]/LearningOptions[] are the real
        // callers' payload type).
        answers: z.array(
          z
            .object({
              id: z.number(),
              value: z.string(),
            })
            .loose()
        ),
      })
      .optional()
  ),
});

export type UpdateSurveyParams = z.infer<typeof updateSurveyParams>;

export async function fetchOnboardingSurvey() {
  const { data } = await http().get(
    `${globalThis.env.API_PROFILE}/surveys?title=Cisco U. EA Onboarding`
  );

  return surveys.parse(data);
}

export async function fetchOnboardingSurveyResponse() {
  const { data } = await http().get(
    `${globalThis.env.API_PROFILE}/surveyresponse?title=Cisco U. EA Onboarding`
  );
  return surveyResponse.parse(data);
}

export function updateSurveyResponse(postObject: UpdateSurveyParams) {
  return http().post(
    `${globalThis.env.API_PROFILE}/surveyresponse`,
    updateSurveyParams.parse(postObject)
  );
}

// POST /user-content-surveys (profile-service) - dto.UserContentSurvey's client-writable fields.
const assignSurveyParams = z.object({
  user_id: z.number(),
  content_id: z.string(),
  survey_id: z.number(),
  status: z.enum(["assigned", "completed", "skipped"]),
});

export function assignUserToSurvey(userId: number, contentId: string, surveyId: number) {
  const postObject = {
    user_id: userId,
    content_id: contentId,
    survey_id: surveyId,
    status: "assigned",
  };

  return http().post(
    `${globalThis.env.API_PROFILE}/user-content-surveys`,
    assignSurveyParams.parse(postObject)
  );
}

// GET /user-content-surveys (profile-service) - inconsistent envelope confirmed live: a bare
// []dto.UserContentSurvey when results exist, or {data: [], message} when there are none.
const userContentSurveyHistoryItem = z.object({ status: z.string() }).loose();
const userContentSurveyHistory = z.union([
  z.array(userContentSurveyHistoryItem),
  z.object({ data: z.array(userContentSurveyHistoryItem) }).loose(),
]);

export async function fetchUserSurveyHistory(user_id: number, content_id: string) {
  const { data } = await http().get(`${globalThis.env.API_PROFILE}/user-content-surveys`, {
    params: {
      user_id,
      content_id,
    },
  });
  const parsed = userContentSurveyHistory.parse(data);
  return Array.isArray(parsed) ? parsed : parsed.data;
}
